import type { EraDef } from '../../config/eras'

export interface EraBlend {
  from: EraDef
  to: EraDef
  /** 0 = pure from, 1 = pure to */
  t: number
}

export function clamp01(x: number): number {
  if (Number.isNaN(x)) return 0
  if (x < 0) return 0
  if (x > 1) return 1
  return x
}

/**
 * Integrate time; hard clamp to [0, 1] — no bounce, no wrap.
 */
export function integrateTime(
  timeValue: number,
  timeSpeed: number,
  dt: number,
): number {
  const safeDt = Number.isFinite(dt) ? Math.max(0, dt) : 0
  const safeSpeed = Number.isFinite(timeSpeed) ? timeSpeed : 0
  return clamp01(timeValue + safeSpeed * safeDt)
}

/**
 * Soft-stop when clamped at endpoint while velocity still points outward.
 */
export function shouldSoftStopAtEndpoint(
  timeValue: number,
  timeSpeed: number,
  eps = 1e-6,
): boolean {
  if (timeValue <= 0 + eps && timeSpeed < 0) return true
  if (timeValue >= 1 - eps && timeSpeed > 0) return true
  return false
}

export function resolveEra(timeValue: number, eras: EraDef[]): EraDef {
  const v = clamp01(timeValue)
  for (const era of eras) {
    const [start, end] = era.range
    if (v >= start && (v < end || end === 1)) {
      if (end === 1 && v === 1) return era
      if (v < end) return era
    }
  }
  return eras[eras.length - 1]
}

/**
 * Smooth blend near era boundaries using blendWidth on each side of the boundary.
 */
export function resolveEraBlend(
  timeValue: number,
  eras: EraDef[],
  blendWidth: number,
): EraBlend {
  const v = clamp01(timeValue)
  if (eras.length === 0) {
    throw new Error('resolveEraBlend: empty eras')
  }
  if (eras.length === 1) {
    return { from: eras[0], to: eras[0], t: 0 }
  }

  const current = resolveEra(v, eras)
  const idx = eras.findIndex((e) => e.id === current.id)
  const half = Math.max(1e-4, blendWidth)

  // Approaching next boundary from below
  if (idx < eras.length - 1) {
    const boundary = current.range[1]
    if (v >= boundary - half && v < boundary + half) {
      const t = clamp01((v - (boundary - half)) / (2 * half))
      return { from: current, to: eras[idx + 1], t }
    }
  }

  // Approaching previous boundary from above
  if (idx > 0) {
    const boundary = current.range[0]
    if (v >= boundary - half && v < boundary + half) {
      const t = clamp01((v - (boundary - half)) / (2 * half))
      // t=0 near previous era, t=1 into current — map so from=prev, to=current
      return { from: eras[idx - 1], to: current, t }
    }
  }

  return { from: current, to: current, t: 0 }
}
